import React, { useState, useEffect } from "react";
import styled from "styled-components";
import ILEARNVideoPlayer from "../../components/ILEARNProgress/ILEARNVideoPlayer";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PDFImage from "../../assets/image/pdf.gif";
import CompleteImage from "../../assets/image/complete.gif";
import API from "../../util/api";
import { RESOURCES_BASE_URL } from "../../util/constant";

const breakpoints = {
  tablet: 768,
  phone: 480,
};

const ILEARNContainer = styled.div`
  padding: 20px;
  width: 1100px;
  min-height: 75vh;
  margin: 0 auto;
  border-radius: 5px;
  background-color: ${(props) => props.colors.colors.formBackground};
  overflow: hidden;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.phone}px) {
    padding: 12px;
    overflow: visible;
  }
`;

const BackButton = styled.button`
  background-color: ${(props) => props.colors.colors.darkBlue};
  color: #fff;
  border: none;
  padding: 8px 15px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  border-radius: 5px;
  margin-bottom: 20px;

  &:hover {
    opacity: 0.9;
  }
`;

const Section = styled.div`
  text-align: center;
  background-color: ${(props) => props.colors.colors.formBackground};
  border-bottom: ${(props) => (props.isLast ? "none" : "1px solid #ccc")};
`;

const Table = styled.table`
  border: 0;
  width: 100%;
  margin: 0 auto;
`;

const TableRow = styled.tr`
  margin-bottom: 10px;
`;

const Title = styled.h2`
  text-align: left;
  font-size: 20px;
  color: ${(props) => props.colors.colors.labelText};
`;

const SubTitle = styled.h3`
  text-align: left;
  font-size: 16px;
  margin-top: 10px;
  color: ${(props) => props.colors.colors.labelText};
`;

const Paragraph = styled.p`
  text-align: left;
  font-size: 14px;
  line-height: 1.6;
  color: ${(props) => props.colors.colors.labelText};
`;

const ChapterHeader = styled.h2`
  text-align: left;
  font-size: 18px;
  margin-bottom: 10px;
  color: ${(props) => props.colors.colors.labelText};
`;

const Links = styled.div`
  text-align: left;
  margin-top: 10px;
`;

const Duration = styled.span`
  font-size: 12px;
  color: ${(props) => props.colors.colors.labelText};
  margin-left: 10px;
`;

const ActionButton = styled.button`
  background-color: ${(props) => props.colors.colors.darkBlue};
  color: #fff;
  border: none;
  padding: 5px 10px;
  font-size: 14px;
  cursor: pointer;
  border-radius: 5px;
  margin-right: 10px;

  &:hover {
    opacity: 0.9;
  }
`;
const PdfLinks = styled.div`
  margin-top: 20px;
  text-align: left;

  a {
    text-decoration: none;
    color: ${(props) => props.colors.colors.linkText};
    font-size: 14px;
    margin-right: 20px;
    display: flex;
    align-items: center;

    &:hover {
      color: ${(props) => props.colors.colors.hoverLinkText};
    }

    img {
      margin-left: 8px;
      width: 16px;
      height: 16px;
    }
  }
`;

const DisabledButton = styled.span`
  background-color: ${(props) => props.colors.colors.buttonBackground};
  color: #fff;
  border: none;
  padding: 5px 10px;
  font-size: 14px;
  border-radius: 5px;
  opacity: 0.5;
`;

const CompleteImageStyled = styled.img`
  width: 75px;
  height: 75px;
  margin-left: 10px;
`;

const ILEARNModule5Page = (colors) => {
  const chapters = useSelector((state) => state.ilearn.chapters)[4];
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [durations, setDurations] = useState({});
  const navigate = useNavigate();
  const openVideoModal = async (chapterNo) => {
    setLoadingVideo(true);
    try {
      const resp = await API.getILearnVideos({
        module_code: "cs",
        chapter_no: chapterNo.id,
      });
      if (
        resp.status === "Success" &&
        Array.isArray(resp.ilean_module_chapters) &&
        resp.ilean_module_chapters.length > 0
      ) {
        const vid = resp.ilean_module_chapters[0];
        setSelectedVideo({
          vimeoId: vid.vimeo_id,
          title: chapterNo.title,
          width: vid.video_width,
          height: vid.video_height,
          chapterNo,
        });
      } else {
        console.warn("No video data returned for chapter", chapterNo);
      }
    } catch (err) {
      console.error("Failed to load video:", err);
    } finally {
      setLoadingVideo(false);
    }
  };
  const closeVideoModal = () => {
    setSelectedVideo(null);
  };
  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = Math.round(seconds % 60)
      .toString()
      .padStart(2, "0");
    return `${m}:${s}`;
  };
  useEffect(() => {
    const loadDurations = async () => {
      try {
        const resp = await API.getILEARNChapter({
          module_code: "cs",
          module_no: 4,
        });
        if (resp.status === "Success" && Array.isArray(resp.videos)) {
          const map = {};
          resp.videos.forEach((v) => {
            // assume `v.duration` is a number of seconds
            map[v.video_chapter] = formatDuration(Number(v.duration));
          });
          setDurations(map);
        }
      } catch (err) {
        console.error("Failed to load durations:", err);
      }
    };
    loadDurations();
  }, []);

  return (
    <ILEARNContainer colors={colors}>
      <BackButton colors={colors} onClick={() => navigate("/ilearn")}>
        Back to ILEARN
      </BackButton>
      {selectedVideo && (
        <ILEARNVideoPlayer
          videoId={selectedVideo.vimeoId}
          title={`Chapter ${selectedVideo.chapterNo.id}: ${selectedVideo.title}`}
          closeModal={closeVideoModal}
          moduleId={4}
          chapterId={selectedVideo.chapterNo.id}
          colors={colors}
        />
      )}

      <Section colors={colors} isLast={false}>
        <Table colors={colors}>
          <tbody>
            <TableRow colors={colors}>
              <td colSpan="2">
                <Title colors={colors}>Cyber Security</Title>
              </td>
              <td>
                {chapters.is_completed == true && (
                  <CompleteImageStyled src={CompleteImage} alt="Completed" />
                )}
              </td>
            </TableRow>
            <TableRow colors={colors}>
              <PdfLinks colors={colors}>
                <td>
                  {" "}
                  <Paragraph colors={colors}>
                    Module Information Resources:
                  </Paragraph>{" "}
                </td>
                <td>
                  <a
                    href={`${RESOURCES_BASE_URL}/Procedures_CS.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Procedures
                    <img
                      src={PDFImage}
                      alt="PDF icon"
                      title="Download Procedures"
                    />
                  </a>
                </td>
                <td>
                  <a
                    href={`${RESOURCES_BASE_URL}/PDP_Activities_CS.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Activities
                    <img
                      src={PDFImage}
                      alt="PDF icon"
                      title="Download Activities"
                    />
                  </a>
                </td>
              </PdfLinks>
            </TableRow>
            <TableRow colors={colors}>
              <td>
                <SubTitle colors={colors}>Step One:</SubTitle>
                <Paragraph colors={colors}>
                  Click "view online" to watch each of the 5 module chapters. To
                  "view online" you must have cable or DSL Internet and
                  Macromedia Flash. If you do not have the free Flash program it
                  will be offered to you when you click "view online."
                </Paragraph>

                <SubTitle colors={colors}>Step Two:</SubTitle>
                <Paragraph colors={colors}>
                  View each chapter in sequential order until all 5 chapters are
                  viewed and this module has been completed in its entirety. You
                  must allow the window with the video to close on its own or
                  you will not receive credit for viewing the entire video. You
                  may view the chapters within your own timeframe but all
                  chapters must be completed before you can move on to the
                  training modules.
                </Paragraph>

                <SubTitle colors={colors}>Step Three:</SubTitle>
                <Paragraph colors={colors}>
                  View each chapter in sequential order until all chapters are
                  viewed and this module has been completed in its entirety. You
                  must allow the window with the video to close on its own or
                  you will not receive credit for viewing the entire video.
                </Paragraph>
              </td>
            </TableRow>
          </tbody>
        </Table>
      </Section>
      <Section id="chapters" colors={colors} isLast={true}>
        <Table>
          <tbody>
            {chapters?.chapters?.map((chapter, index) => (
              <TableRow key={chapter.chapter_no}>
                <td>
                  <Table>
                    <tbody>
                      <TableRow colors={colors}>
                        <td>
                          <ChapterHeader colors={colors}>
                            Chapter {chapter.chapter_no}: {chapter.chapter_name}
                          </ChapterHeader>
                        </td>
                      </TableRow>
                      <TableRow>
                        <td>
                          <Links>
                            {index === 0 ||
                            chapters?.chapters?.[index - 1]?.is_completed ? (
                              <>
                                <ActionButton
                                  colors={colors}
                                  onClick={() =>
                                    openVideoModal({
                                      videoId: chapter.video_id,
                                      id: chapter.chapter_no,
                                      title: chapter.chapter_name,
                                    })
                                  }
                                >
                                  {chapters?.chapters?.[index]?.is_completed
                                    ? "Completed"
                                    : "View Online"}
                                </ActionButton>
                                <Duration colors={colors}>
                                  {durations[chapter.chapter_no]
                                    ? `(${durations[chapter.chapter_no]})`
                                    : ""}
                                </Duration>
                              </>
                            ) : (
                              <>
                                <DisabledButton colors={colors}>
                                  Please watch Chapter {chapter.chapter_no - 1}{" "}
                                  before.
                                </DisabledButton>
                                <Duration colors={colors}>
                                  {durations[chapter.chapter_no]
                                    ? `(${durations[chapter.chapter_no]})`
                                    : ""}
                                </Duration>
                              </>
                            )}
                          </Links>
                        </td>
                      </TableRow>
                    </tbody>
                  </Table>
                </td>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </Section>
    </ILEARNContainer>
  );
};

export default ILEARNModule5Page;
